import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import RevealHeading from '../common/RevealHeading';

const StatCounter = ({ end, label, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById(`stat-${label}`);
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [label]);

  useEffect(() => {
    if (!inView) return;

    const duration = 2;
    const steps = 60;
    const increment = end / steps;
    let current = 0;

    const interval = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(interval);
      } else {
        setCount(Math.floor(current));
      }
    }, (duration * 1000) / steps);

    return () => clearInterval(interval);
  }, [inView, end]);

  return (
    <motion.div
      id={`stat-${label}`}
      whileHover={{ scale: 1.05 }}
      className="flex flex-col items-center gap-2 p-6 rounded-lg bg-dark-800/50 border border-primary-500/20 hover:border-primary-500/50 transition-all duration-300"
    >
      <div className="text-3xl sm:text-4xl font-bold text-transparent bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text">
        {count.toLocaleString()}
        {suffix}
      </div>
      <div className="text-sm sm:text-base text-white/70 font-medium text-center">{label}</div>
    </motion.div>
  );
};

const StatsSection = () => {
  const stats = [
    { end: 30, label: 'Projects Completed', suffix: '+' },
    { end: 15, label: 'ML Models Trained', suffix: '+' },
    { end: 3, label: 'Years of Experience', suffix: '+' },
    { end: 20, label: 'Clients Served', suffix: '+' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 relative overflow-hidden">
      <div className="container-custom">
        {/* Background Elements */}
        <motion.div
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute -top-40 -right-40 w-80 h-80 bg-accent-500 rounded-full blur-3xl opacity-10"
        />

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16 relative z-10"
        >
          <RevealHeading text="By the Numbers" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" />
          <p className="text-white/60 text-base sm:text-lg max-w-2xl mx-auto">
            A snapshot of my work and experience so far
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 relative z-10"
        >
          {stats.map((stat, idx) => (
            <StatCounter key={idx} {...stat} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default StatsSection;
